import { useSelector } from 'react-redux';
import { useMemo } from 'react';

/**
 * Hook to check if a feature is enabled for the current brand
 *
 * @param {string} featureSlug - The feature slug to check (e.g., 'gwx-ai-biz-suite-msg-solns-wapp-bcast')
 * @returns {Object} Feature information
 * @returns {boolean} isEnabled - Whether the feature is enabled
 * @returns {boolean} isLoading - Whether the configuration is loading
 * @returns {Object|null} feature - The complete feature object or null
 * @returns {boolean} isChargeable - Whether the feature is chargeable
 * @returns {string} charges - The charges for the feature
 * @returns {number|null} limit - The limit for the feature
 * @returns {string|null} source - Where the config came from ('persisted' | 'api' | 'stale-cache' | 'default')
 *
 * @example
 * const { isEnabled, isLoading, feature } = useFeature(FEATURES.BROADCAST_CAMPAIGN);
 *
 * if (isLoading) return <Spin />;
 * if (!isEnabled) return <UpgradePrompt />;
 * return <BroadcastUI />;
 */
export const useFeature = (featureSlug) => {
  const { featuresMap, loading, source } = useSelector(
    (state) => state.app.brandPlanConfig,
  );

  return useMemo(() => {
    const feature = featuresMap[featureSlug];

    return {
      isEnabled: feature?.is_enabled ?? false,
      isLoading: loading,
      feature: feature || null,
      isChargeable: feature?.is_chargeable ?? false,
      charges: feature?.charges,
      limit: feature?.limit,
      source,
    };
  }, [featuresMap, featureSlug, loading, source]);
};

/**
 * Hook to check if a function is enabled for the current brand
 *
 * @param {string} functionSlug - The function slug to check (e.g., 'gwx-ai-biz-suite-msg-solns-wapp-bcast-send-now')
 * @returns {Object} Function information
 * @returns {boolean} isEnabled - Whether the function is enabled
 * @returns {boolean} isLoading - Whether the configuration is loading
 * @returns {Object|null} function - The complete function object or null
 * @returns {boolean} isChargeable - Whether the function is chargeable
 * @returns {string} charges - The charges for the function
 * @returns {number|null} limit - The limit for the function
 * @returns {string|null} period - The period for limits (e.g., 'monthly', 'lifetime')
 * @returns {string|null} metricType - The metric type (e.g., 'count', 'duration')
 * @returns {string|null} unitOfMeasure - The unit of measure (e.g., 'messages', 'nodes')
 * @returns {string|null} source - Where the config came from
 *
 * @example
 * const { isEnabled, limit, metricType } = useFunction(FUNCTIONS.BROADCAST_SEND_NOW);
 *
 * return (
 *   <Button disabled={!isEnabled} onClick={handleSend}>
 *     Send Now
 *   </Button>
 * );
 */
export const useFunction = (functionSlug) => {
  const { functionsMap, loading, source } = useSelector(
    (state) => state.app.brandPlanConfig,
  );

  return useMemo(() => {
    const func = functionsMap[functionSlug];

    return {
      isEnabled: func?.is_enabled ?? false,
      isLoading: loading,
      function: func || null,
      isChargeable: func?.is_chargeable ?? false,
      charges: func?.charges,
      limit: func?.limit,
      period: func?.period,
      metricType: func?.metric_type,
      unitOfMeasure: func?.unit_of_measure,
      source,
    };
  }, [functionsMap, functionSlug, loading, source]);
};

/**
 * Hook to check multiple features at once
 *
 * @param {string[]} featureSlugs - Array of feature slugs to check
 * @returns {Object} Features information
 * @returns {Object} features - Map of slug -> isEnabled
 * @returns {boolean} isLoading - Whether the configuration is loading
 * @returns {boolean} allEnabled - Whether all features are enabled
 * @returns {boolean} anyEnabled - Whether any feature is enabled
 *
 * @example
 * const { features, allEnabled, anyEnabled } = useFeatures([
 *   FEATURES.BROADCAST_CAMPAIGN,
 *   FEATURES.SEQUENCE_CAMPAIGN,
 * ]);
 *
 * if (anyEnabled) {
 *   // Show campaigns section
 * }
 */
export const useFeatures = (featureSlugs = []) => {
  const { featuresMap, loading } = useSelector(
    (state) => state.app.brandPlanConfig,
  );

  return useMemo(() => {
    const results = {};
    featureSlugs.forEach((slug) => {
      results[slug] = featuresMap[slug]?.is_enabled ?? false;
    });

    return {
      features: results,
      isLoading: loading,
      allEnabled: Object.values(results).every((enabled) => enabled),
      anyEnabled: Object.values(results).some((enabled) => enabled),
    };
  }, [featuresMap, featureSlugs, loading]);
};

/**
 * Hook to check multiple functions at once
 *
 * @param {string[]} functionSlugs - Array of function slugs to check
 * @returns {Object} Functions information
 * @returns {Object} functions - Map of slug -> isEnabled
 * @returns {boolean} isLoading - Whether the configuration is loading
 * @returns {boolean} allEnabled - Whether all functions are enabled
 * @returns {boolean} anyEnabled - Whether any function is enabled
 *
 * @example
 * const { functions, allEnabled } = useFunctions([
 *   FUNCTIONS.BROADCAST_SEND_NOW,
 *   FUNCTIONS.BROADCAST_SEND_LATER,
 * ]);
 *
 * if (allEnabled) {
 *   // Show all broadcast options
 * }
 */
export const useFunctions = (functionSlugs = []) => {
  const { functionsMap, loading } = useSelector(
    (state) => state.app.brandPlanConfig,
  );

  return useMemo(() => {
    const results = {};
    functionSlugs.forEach((slug) => {
      results[slug] = functionsMap[slug]?.is_enabled ?? false;
    });

    return {
      functions: results,
      isLoading: loading,
      allEnabled: Object.values(results).every((enabled) => enabled),
      anyEnabled: Object.values(results).some((enabled) => enabled),
    };
  }, [functionsMap, functionSlugs, loading]);
};

/**
 * Hook to get the current brand plan configuration info
 *
 * @returns {Object} Configuration information
 * @returns {Object|null} brand - Current brand info
 * @returns {Object|null} billingPlan - Current billing plan info
 * @returns {string|null} source - Where the config came from
 * @returns {number|null} cacheExpiry - When the cache expires (timestamp)
 * @returns {boolean} isExpired - Whether the cache is expired
 * @returns {number|null} cacheAgeHours - Age of cache in hours
 *
 * @example
 * const { brand, billingPlan, source, isExpired } = useBrandPlanInfo();
 *
 * return (
 *   <div>
 *     <p>Plan: {billingPlan?.name}</p>
 *     {isExpired && <Alert message="Configuration may be outdated" />}
 *   </div>
 * );
 */
export const useBrandPlanInfo = () => {
  const { brand, billingPlan, source, cacheExpiry, lastFetched } = useSelector(
    (state) => state.app.brandPlanConfig,
  );

  return useMemo(() => {
    const now = Date.now();
    const isExpired = cacheExpiry ? now >= cacheExpiry : false;
    const cacheAgeHours = lastFetched
      ? Math.floor((now - lastFetched) / (1000 * 60 * 60))
      : null;

    return {
      brand,
      billingPlan,
      source,
      cacheExpiry,
      isExpired,
      cacheAgeHours,
    };
  }, [brand, billingPlan, source, cacheExpiry, lastFetched]);
};
